import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown, DropdownToggle, DropdownMenu, Spinner, Alert, Row, Col } from 'reactstrap';
import SimpleBar from 'simplebar-react';
import { withTranslation } from 'react-i18next';

import { useListQuery } from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';

const ShopsDropdown = ({ t }) => {
  const [menu, setMenu] = useState(false);
  const [selectedShopId, setSelectedShopId] = useState(null);
  const navigate = useNavigate();

  const { data: shops, isLoading, isError } = useListQuery(`${API_ENDPOINTS.TENANT.SHOPS.BASE}`);

  // Load saved shop from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('selectedShopId');
      if (saved) {
        setSelectedShopId(saved);
      }
    } catch (e) {
      localStorage.removeItem('selectedShopId');
    }
  }, []);

  // Validate selected shop against loaded shops and update if necessary
  useEffect(() => {
    if (!shops || !selectedShopId) return;
    const shopExists = shops.some((s) => s.id === selectedShopId);
    if (!shopExists) {
      setSelectedShopId(null);
      localStorage.removeItem('selectedShopId');
    }
  }, [shops, selectedShopId]);

  const handleShopSelect = (shopId) => {
    setSelectedShopId(shopId);
    localStorage.setItem('selectedShopId', shopId);
    navigate(`/shops/${shopId}/sells-entry`);
    setMenu(false); // Close the dropdown
  };

  const selectedShop = shops?.find((shop) => shop.id === selectedShopId);

  return (
    <React.Fragment>
      <Dropdown
        isOpen={menu}
        toggle={() => setMenu(!menu)}
        className="dropdown d-inline-block"
        tag="li"
      >
        <DropdownToggle
          className="btn header-item noti-icon position-relative"
          tag="button"
          id="page-header-shops-dropdown"
        >
          <i className="bx bx-store" />
          {selectedShop ? (
            <span className="ms-1">{selectedShop.name}</span>
          ) : (
            <span className="ms-1 text-muted small">{t('Select Shop')}</span>
          )}
          {shops?.length > 0 && <span className="badge bg-info rounded-pill">{shops.length}</span>}
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu dropdown-menu-lg dropdown-menu-end p-0">
          <div className="p-3">
            <Row className="align-items-center">
              <Col>
                <h6 className="m-0">{t('Your Shops')}</h6>
              </Col>
              <div className="col-auto">
                <Link to="/shops" className="small">
                  {t('View All')}
                </Link>
              </div>
            </Row>
          </div>

          <SimpleBar style={{ height: '230px' }}>
            {isLoading && (
              <div className="text-center p-3">
                <Spinner size="sm" color="primary" /> {t('Loading...')}
              </div>
            )}

            {isError && (
              <div className="text-center p-3">
                <Alert color="danger" className="mb-0">
                  {t('Failed to load shops')}
                </Alert>
              </div>
            )}

            {!isLoading && !isError && shops?.length === 0 && (
              <div className="text-center p-3">
                <span className="text-muted">{t('No shops found')}</span>
              </div>
            )}

            {!isLoading &&
              !isError &&
              shops?.map((shop) => (
                <div
                  key={shop.id}
                  className="text-reset notification-item"
                  onClick={() => handleShopSelect(shop.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="d-flex">
                    <div className="avatar-xs me-3">
                      <span className="avatar-title bg-secondary rounded-circle font-size-16">
                        <i className="bx bx-store-alt" />
                      </span>
                    </div>
                    <div className="flex-grow-1">
                      <h6
                        className={`mt-0 mb-1 ${shop.id === selectedShopId ? 'text-primary' : ''}`}
                      >
                        {shop.name}
                      </h6>
                      <div className="font-size-12 text-muted">
                        <p className="mb-0">
                          <i className="mdi mdi-map-marker me-1" />
                          {shop.city}, {shop.state}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </SimpleBar>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

ShopsDropdown.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(ShopsDropdown);
